/* eslint-disable no-param-reassign */

import * as ModelEvent from "@node-elion/syncron";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import Subscription from "../../types/Subscription";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { Base, Language, CarPark } from "..";
import ExecutorRateCommissionPlan from "../ExecutorRateCommissionPlan";
import { TransportOwnershipType } from "../Car";

const logger = createLogger({ name: "CarParkSettings" });

type CommissionType = ExecutorRateCommissionPlan.Model.Rate.Commission.Type;

const FLEET_FEE_OWNERSHIP_KEYS: (keyof CarParkSettings.Fee)[] = [
	TransportOwnershipType.FLEET_TRANSPORT,
	TransportOwnershipType.FLEET_TRANSPORT_BRAND,
	TransportOwnershipType.PERSONAL_TRANSPORT,
	TransportOwnershipType.PERSONAL_TRANSPORT_BRAND,
];

const FILTER_ITEMS: string[] = [
	"cardPaymentToTaxiService",
	"cardPaymentToExecutor",
	"terminalToTaxiService",
	"terminalToExecutor",
	"externalTransfer",
];

const conditionOperatorMaps = {
	fromResponse: {
		greater: "greater",
		less: "less",
		equal: "equal",
		greaterEqual: "greaterOrEqual",
		lessEqual: "lessOrEqual",
	},
	toRequest: {
		greater: "greater",
		less: "less",
		equal: "equal",
		greaterOrEqual: "greaterEqual",
		lessOrEqual: "lessEqual",
	},
} as const;

const PAYMENT_TYPE_TO_COMMISSION: Record<string, CommissionType> = {
	cash: ExecutorRateCommissionPlan.Model.Rate.Commission.Type.CashOrders,
	card: ExecutorRateCommissionPlan.Model.Rate.Commission.Type
		.CardPaymentToTaxiService,
	company_bill:
		ExecutorRateCommissionPlan.Model.Rate.Commission.Type.NonCashOrders,
};

const defaultCondition = (): CarParkSettings.FeeCommission["condition"] => ({
	active: false,
	type: ExecutorRateCommissionPlan.Model.Rate.Commission.Threshold.Comparison
		.Direction.Equal,
	value: 0,
	percent: 0,
	amount: 0,
});

const defaultFeeCommission = (
	type: CommissionType,
	patch?: Partial<CarParkSettings.FeeCommission>,
): CarParkSettings.FeeCommission => ({
	id: type,
	percent: patch?.percent ?? 0,
	amount: patch?.amount ?? 0,
	condition: {
		...defaultCondition(),
		...patch?.condition,
	},
});

const createDefaultOwnershipCommissions = (): CarParkSettings.FeeCommission[] =>
	Object.values(ExecutorRateCommissionPlan.Model.Rate.Commission.Type)
		.filter((type) => !FILTER_ITEMS.includes(type))
		.map((type) => defaultFeeCommission(type));

const createDefaultCarParkSettingsFee = (): CarParkSettings.Fee => ({
	fleetTransport: createDefaultOwnershipCommissions(),
	fleetTransportBrand: createDefaultOwnershipCommissions(),
	personalTransport: createDefaultOwnershipCommissions(),
	personalTransportBrand: createDefaultOwnershipCommissions(),
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
	Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isLegacyFlatFee = (block: unknown): boolean => {
	if (!isRecord(block)) return false;
	return (
		typeof block.active === "boolean" &&
		!("cash" in block) &&
		!("commissions" in block) &&
		!("cashOrders" in block)
	);
};

const isPaymentTypeFee = (block: unknown): boolean => {
	if (!isRecord(block)) return false;
	if ("commissions" in block || "commissionSortingOrder" in block) {
		return false;
	}
	return (
		"cash" in block ||
		"card" in block ||
		"company_bill" in block ||
		"invoice" in block
	);
};

const itemFromCommissionCell = (
	type: CommissionType,
	commission: any,
): CarParkSettings.FeeCommission => {
	if (!commission) return defaultFeeCommission(type);
	return defaultFeeCommission(type, {
		percent: Number(commission.percent) || 0,
		amount: Number(commission.amount) || 0,
		condition: {
			active: Boolean(commission.threshold?.active),
			type: (conditionOperatorMaps.fromResponse[
				commission.threshold?.comparison
					?.direction as keyof typeof conditionOperatorMaps.fromResponse
			] ||
				ExecutorRateCommissionPlan.Model.Rate.Commission.Threshold
					.Comparison.Direction.Equal) as
				| ExecutorRateCommissionPlan.Model.Rate.Commission.Threshold.Comparison.Direction
				| undefined,
			value: Number(commission.threshold?.comparison?.value) || 0,
			percent: Number(commission.threshold?.fee?.percent) || 0,
			amount: Number(commission.threshold?.fee?.amount) || 0,
		},
	});
};

const fromPaymentTypeBlock = (
	block: Record<string, any>,
): CarParkSettings.FeeCommission[] => {
	const next = createDefaultOwnershipCommissions();
	Object.entries(PAYMENT_TYPE_TO_COMMISSION).forEach(
		([paymentType, commissionType]) => {
			const cell = block[paymentType];
			if (!cell?.active) return;
			const index = next.findIndex((item) => item.id === commissionType);
			if (index < 0) return;
			next[index] = defaultFeeCommission(commissionType, {
				percent: Number(cell.percent) || 0,
				amount: Number(cell.amount) || 0,
			});
		},
	);
	return next;
};

const fromCommissionBlock = (
	block: Record<string, any>,
): CarParkSettings.FeeCommission[] => {
	const commissions = isRecord(block.commissions) ? block.commissions : block;
	const sorting: CommissionType[] = Array.isArray(
		block.commissionSortingOrder,
	)
		? block.commissionSortingOrder.filter(
				(type: string) =>
					!FILTER_ITEMS.includes(type) &&
					Object.values(
						ExecutorRateCommissionPlan.Model.Rate.Commission.Type,
					).includes(type as CommissionType),
		  )
		: [];
	const ordered = (
		sorting.length
			? sorting
			: Object.values(
					ExecutorRateCommissionPlan.Model.Rate.Commission.Type,
			  )
	) as CommissionType[];
	const seen = new Set<string>();
	const items = ordered
		.filter((type) => !FILTER_ITEMS.includes(type))
		.map((type) => {
			seen.add(type);
			return itemFromCommissionCell(type, commissions[type]);
		});
	Object.values(
		ExecutorRateCommissionPlan.Model.Rate.Commission.Type,
	).forEach((type) => {
		if (seen.has(type) || FILTER_ITEMS.includes(type)) return;
		items.push(itemFromCommissionCell(type, commissions[type]));
	});
	return items;
};

const normalizeOwnershipFee = (
	block: unknown,
): CarParkSettings.FeeCommission[] => {
	if (Array.isArray(block) && block.length) {
		const defaults = createDefaultOwnershipCommissions();
		const allowed = new Set(defaults.map((item) => item.id));
		const fromValue = block
			.filter((item) => allowed.has(item.id))
			.map((item) => defaultFeeCommission(item.id, item));
		const seen = new Set(fromValue.map((item) => item.id));
		defaults.forEach((item) => {
			if (!seen.has(item.id)) {
				fromValue.push(item);
			}
		});
		return fromValue;
	}
	if (isLegacyFlatFee(block)) {
		const record = block as Record<string, any>;
		return fromPaymentTypeBlock({
			cash: record,
			card: record,
			company_bill: record,
			invoice: record,
		});
	}
	if (isPaymentTypeFee(block)) {
		return fromPaymentTypeBlock(block as Record<string, any>);
	}
	if (isRecord(block)) {
		return fromCommissionBlock(block);
	}
	return createDefaultOwnershipCommissions();
};

const normalizeCarParkSettingsFee = (
	fee?: CarParkSettings.Fee | null,
): CarParkSettings.Fee => {
	const source = (fee || {}) as Record<string, unknown>;
	return {
		fleetTransport: normalizeOwnershipFee(source.fleetTransport),
		fleetTransportBrand: normalizeOwnershipFee(source.fleetTransportBrand),
		personalTransport: normalizeOwnershipFee(source.personalTransport),
		personalTransportBrand: normalizeOwnershipFee(
			source.personalTransportBrand,
		),
	};
};

const ownershipToRequest = (items: CarParkSettings.FeeCommission[]) => {
	const visible = items.filter((item) => !FILTER_ITEMS.includes(item.id));
	return {
		commissionSortingOrder: visible.map((item) => item.id),
		commissions: visible.reduce(
			(acc, commission) => ({
				...acc,
				[commission.id]: {
					percent: commission.percent,
					amount: commission.amount,
					threshold: {
						active: Boolean(commission.condition?.active),
						comparison: {
							direction:
								conditionOperatorMaps.toRequest[
									(commission.condition?.type ||
										"equal") as keyof typeof conditionOperatorMaps.toRequest
								] || "equal",
							value: Number(commission.condition?.value) || 0,
						},
						fee: {
							percent: Number(commission.condition?.percent) || 0,
							amount: Number(commission.condition?.amount) || 0,
						},
					},
				},
			}),
			{},
		),
	};
};

const feeToRequest = (fee: CarParkSettings.Fee) => {
	const normalized = normalizeCarParkSettingsFee(fee);
	return {
		fleetTransport: ownershipToRequest(normalized.fleetTransport),
		fleetTransportBrand: ownershipToRequest(normalized.fleetTransportBrand),
		personalTransport: ownershipToRequest(normalized.personalTransport),
		personalTransportBrand: ownershipToRequest(
			normalized.personalTransportBrand,
		),
	};
};

class CarParkSettings extends Base {
	public static readonly FLEET_FEE_OWNERSHIP_KEYS = FLEET_FEE_OWNERSHIP_KEYS;

	public static createDefaultFee = createDefaultCarParkSettingsFee;

	public static normalizeFee = normalizeCarParkSettingsFee;

	public static fromResponse(data: any): CarParkSettings.Model {
		const retval: CarParkSettings.Model = {
			id: data.id,
			name: data.name,
			active: data.active,
			activeBalance: data.balance_executor ? "executor" : "fleet",
			companyId: data.companyId,
			fleetId: data.fleet?.id ?? null,
			taxiServiceId: data.taxiServiceId,
			executorRateIds:
				data.fleetSettingsToExecutorRate?.map(
					(r: any) => r.executor_rate_id,
				) ?? [],
			fee: normalizeCarParkSettingsFee(data.fee),
			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
		};

		return retval;
	}

	public static toRequest(
		model: CarParkSettings.Model.New | CarParkSettings.Model.Modified,
	): any {
		return {
			name: model.name,
			active: model.active,
			activeBalance: model.activeBalance,
			companyId: model.companyId,
			fleetId: model.fleetId ?? null,
			taxiServiceId: model.taxiServiceId,
			executorRateIds: model.executorRateIds,
			fee: model.fee ? feeToRequest(model.fee) : undefined,
		};
	}

	public static async store(object: CarParkSettings.Model.New) {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.fleet.settings.create(this.toRequest(object)),
			);
			logger.info("[CarParkSettings] store", { object, res });
			return { status: true, res };
		} catch (err) {
			logger.error("[CarParkSettings] Error store:", { object, err });
			return { status: false, err };
		}
	}

	public static async update(object: CarParkSettings.Model.Modified) {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.fleet.settings.update(
					object.id,
					this.toRequest(object),
				),
			);
			logger.info("[CarParkSettings] update", { object, res });
			return { status: true, res };
		} catch (err) {
			logger.error("[CarParkSettings] Error update:", { object, err });
			return { status: false, err };
		}
	}

	public static async destroy(id: number | number[]) {
		try {
			const res = Array.isArray(id)
				? await Promise.all(id.map((oneId) => this.destroyOne(oneId)))
				: await this.destroyOne(id);

			logger.info("[CarParkSettings] destroy", { id, res });
		} catch (err) {
			logger.error("[CarParkSettings] Error destroy:", { id, err });
		}
	}

	public static async subscribe(
		options: CarParkSettings.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<CarParkSettings.Model>,
	): Promise<Subscription<CarParkSettings.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				onUpdate({
					...state,

					models: state.models.map(this.fromResponse),
				});

				logger.info(
					"[CarParkSettings] subscribe",
					options,
					state.models,
				);
			},
		});
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.fleet.settings.subscribe({
					params: this.optionsToRequest(options),
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "CarParkSettings.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: CarParkSettings.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(options)),
		} as Subscription<CarParkSettings.SubscribeOptions>;
	}

	private static async destroyOne(id: number) {
		return this.request((prpc) =>
			prpc.theirsModel.fleet.settings.delete(id),
		);
	}

	private static optionsToRequest(options: CarParkSettings.SubscribeOptions) {
		return {
			taxiServiceIds: options.taxiServiceIds,
		};
	}
}

declare namespace CarParkSettings {
	interface FeeCommission {
		id: ExecutorRateCommissionPlan.Model.Rate.Commission.Type;
		percent: number;
		amount: number;
		condition: {
			active?: boolean;
			type?: ExecutorRateCommissionPlan.Model.Rate.Commission.Threshold.Comparison.Direction;
			value?: number;
			percent: number;
			amount: number;
		};
	}

	interface Fee {
		fleetTransport: FeeCommission[];
		fleetTransportBrand: FeeCommission[];
		personalTransport: FeeCommission[];
		personalTransportBrand: FeeCommission[];
	}

	interface Model {
		id: number;
		name: string;
		active: boolean;
		activeBalance: "executor" | "fleet";
		fee: Fee;
		fleetId: number | null;
		fleetCarPark?: CarPark.Model;
		companyId: number;
		taxiServiceId: number;
		executorRateIds: number[];
		executorRateNames?: string[];
		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	interface SubscribeOptions
		extends Omit<ServiceSubscribeOptionsBase<Model>, "order"> {
		limit?: number;
		offset?: number;
		query?: string;
		active?: boolean;
		taxiServiceIds?: number[];
		fleetIds?: number[];
		language?: Language;
	}

	namespace Model {
		type NonEditableProperty =
			| "id"
			| "createdAt"
			| "updatedAt"
			| "deletedAt";

		type New = Omit<Model, NonEditableProperty>;

		type Modified = Partial<Omit<Model, NonEditableProperty>> & {
			id: number;
		};
	}
}

export default CarParkSettings;
